import { useEffect, useRef, useState } from "react";
import { Send, X, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

/* 🤖 VTS Coach — floating multilingual help chat (all pages). */
const safe = (fn, fb = null) => { try { return fn(); } catch { return fb; } };

const STARTERS = {
  en: ["How do I start?", "Is it free?", "How does Penalty work?", "Best way to use the bots?"],
  pt: ["Como eu começo?", "É grátis?", "Como funciona o Pênalti?", "Melhor jeito de usar os bots?"],
  es: ["¿Cómo empiezo?", "¿Es gratis?", "¿Cómo funciona Penales?", "¿Mejor forma de usar los bots?"],
  fr: ["Comment je commence ?", "C'est gratuit ?", "Comment marche Penalty ?", "Bien utiliser les bots ?"],
};
const UI = {
  en: { title: "VTS Coach", sub: "ask anything — any language", ph: "Type your question…", hi: "Hey! 👋 I'm your VTS Coach. Ask me how anything works — bots, Penalty, Heli, Red/Black, strategies — in your own language." },
  pt: { title: "VTS Coach", sub: "pergunte qualquer coisa", ph: "Digite sua pergunta…", hi: "Olá! 👋 Sou o VTS Coach. Pergunte como funciona qualquer coisa — bots, Pênalti, Heli, estratégias — no seu idioma." },
  es: { title: "VTS Coach", sub: "pregunta lo que sea", ph: "Escribe tu pregunta…", hi: "¡Hola! 👋 Soy el VTS Coach. Pregúntame cómo funciona todo — bots, Penales, Heli, estrategias — en tu idioma." },
  fr: { title: "VTS Coach", sub: "pose ta question", ph: "Écris ta question…", hi: "Salut ! 👋 Je suis le VTS Coach. Demande-moi comment tout fonctionne — bots, Penalty, Héli, stratégies — dans ta langue." },
};

const URL_RE = /(https?:\/\/[^\s]+)/g;
const Linkify = ({ text }) => (
  <>
    {text.split(URL_RE).map((part, i) =>
      /^https?:\/\//.test(part) ? (
        <a key={i} data-testid="helpbot-link" href={part} target="_blank" rel="noreferrer"
          className="text-vts-blue underline break-all font-bold">
          {part.includes("deriv") || part.includes("vtstradingbot") ? "👉 Sign up here" : part}
        </a>
      ) : (
        <span key={i}>{part}</span>
      )
    )}
  </>
);

const sessionId = () => {
  let id = safe(() => localStorage.getItem("vts_helpbot_sid"));
  if (!id) {
    id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    safe(() => localStorage.setItem("vts_helpbot_sid", id));
  }
  return id;
};

const HelpBot = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const dragRef = useRef(null);
  const [pos, setPos] = useState(() => {
    const p = safe(() => JSON.parse(localStorage.getItem("vts_coach_pos")));
    if (!p || typeof p.x !== "number" || typeof p.y !== "number") return null;
    return {
      x: Math.min(Math.max(p.x, 8), window.innerWidth - 64),
      y: Math.min(Math.max(p.y, 70), window.innerHeight - 140),
    };
  });
  const lang = safe(() => localStorage.getItem("vts_lang")) || "en";
  const t = UI[lang] || UI.en;
  const starters = STARTERS[lang] || STARTERS.en;

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "u", text: q }]);
    setBusy(true);
    try {
      const sess = safe(() => JSON.parse(localStorage.getItem("vts_session_v1"))) || {};
      const r = await api.post("/helpbot/chat", {
        session_id: sessionId(), message: q, lang,
        account_id: sess.account_id || null,
        balance: typeof sess.balance === "number" ? sess.balance : null,
        currency: sess.currency || null,
      });
      setMsgs((m) => [...m, { role: "a", text: r.data.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "a", text: "⚠️ Connection hiccup — try again in a moment." }]);
    } finally {
      setBusy(false);
    }
  };

  const onPointerDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX, startY: e.clientY,
      offX: e.clientX - rect.left, offY: e.clientY - rect.top,
      moved: false,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    if (Math.abs(e.clientX - d.startX) + Math.abs(e.clientY - d.startY) > 8) d.moved = true;
    if (d.moved) setPos({ x: e.clientX - d.offX, y: e.clientY - d.offY });
  };
  const onPointerUp = (e) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d) return;
    if (!d.moved) { setOpen(true); return; }
    const size = 56;
    const x = e.clientX - d.offX;
    const y = Math.min(Math.max(e.clientY - d.offY, 70), window.innerHeight - size - 90);
    const snapped = { x: x + size / 2 < window.innerWidth / 2 ? 10 : window.innerWidth - size - 10, y };
    setPos(snapped);
    safe(() => localStorage.setItem("vts_coach_pos", JSON.stringify(snapped)));
  };

  return (
    <>
      {!open && (
        <button data-testid="helpbot-bubble"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          aria-label="Open VTS Coach"
          style={pos ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto", touchAction: "none" } : { touchAction: "none" }}
          className="fixed bottom-44 right-3 md:bottom-6 md:right-6 z-[45] group cursor-grab active:cursor-grabbing select-none">
          <span className="absolute inset-0 rounded-full bg-vts-blue/40 animate-ping" style={{ animationDuration: "2.5s" }} />
          <span className="relative flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-vts-blue to-indigo-500 text-2xl shadow-xl shadow-vts-blue/40 border-2 border-white/20 group-hover:scale-110 transition-transform">
            🤖
          </span>
          <span className="absolute -top-5 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-black/80 border border-vts-blue/50 text-[9px] font-mono font-bold text-vts-blue whitespace-nowrap pointer-events-none">
            {t.title}
          </span>
        </button>
      )}
      {open && (
        <div data-testid="helpbot-panel"
          className="fixed bottom-28 right-3 md:bottom-6 md:right-6 z-[80] w-[min(92vw,22rem)] h-[min(62vh,30rem)] flex flex-col rounded-2xl border border-vts-line bg-vts-panel shadow-2xl overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2.5 bg-vts-surface border-b border-vts-line">
            <Sparkles className="w-4 h-4 text-vts-blue" />
            <div className="leading-tight">
              <p className="text-xs font-bold text-white font-mono">🤖 {t.title}</p>
              <p className="text-[9px] text-vts-silver/60 font-mono">{t.sub}</p>
            </div>
            <button data-testid="helpbot-close" onClick={() => setOpen(false)} className="ml-auto p-1.5 rounded-md hover:bg-vts-line transition">
              <X className="w-4 h-4 text-vts-silver/70" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5">
            <div className="max-w-[88%] rounded-xl rounded-tl-sm bg-vts-surface border border-vts-line px-3 py-2 text-[11.5px] text-vts-silver/90 whitespace-pre-wrap">{t.hi}</div>
            {msgs.length === 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {starters.map((s) => (
                  <button key={s} data-testid="helpbot-starter" onClick={() => send(s)}
                    className="px-2.5 py-1.5 rounded-full border border-vts-blue/40 bg-vts-blue/10 text-[10px] font-mono text-vts-blue hover:bg-vts-blue/20 transition">
                    {s}
                  </button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={m.role === "u"
                ? "ml-auto max-w-[88%] rounded-xl rounded-tr-sm bg-vts-blue/15 border border-vts-blue/30 px-3 py-2 text-[11.5px] text-white whitespace-pre-wrap"
                : "max-w-[88%] rounded-xl rounded-tl-sm bg-vts-surface border border-vts-line px-3 py-2 text-[11.5px] text-vts-silver/90 whitespace-pre-wrap"}>
                {m.role === "a" ? <Linkify text={m.text} /> : m.text}
              </div>
            ))}
            {busy && <div className="text-[10px] font-mono text-vts-silver/50 animate-pulse">🤖 …</div>}
            <div ref={endRef} />
          </div>
          <div className="flex items-center gap-1.5 p-2 border-t border-vts-line bg-vts-surface">
            <input data-testid="helpbot-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={t.ph} maxLength={500}
              className="flex-1 h-9 rounded-lg border border-vts-line bg-vts-panel px-3 text-xs text-white placeholder:text-vts-silver/40 outline-none focus:border-vts-blue/50" />
            <button data-testid="helpbot-send" onClick={() => send()} disabled={busy}
              className="h-9 w-9 grid place-items-center rounded-lg bg-vts-blue text-black disabled:opacity-50 hover:brightness-110 transition">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpBot;
